/**
 * Report QC on the run page (2026-10-22, Joan's Corning / IDT review) —
 * internal view only.
 *
 * Shows whether the client PDF can go out: the checks that stand between the
 * figures and the deliverable (narrative written from these figures and
 * matching them, one denominator, stage sums, report totals = the sources
 * workbook, links checked, the client never named as a rival, leaders named
 * right), what failed with the expected-vs-found figures, and the domains
 * still needing a category. An admin or manager can accept a failed check
 * with a reason (it stands until the figures change), reject a report, queue
 * a full refresh, or re-run the checks. Staff can download a DRAFT-stamped
 * PDF to look at; the client never gets one.
 */
import { useCallback, useEffect, useState } from 'react';
import type { ReportQcFinding, ReportQcPayload } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { useAuth } from '../auth/AuthContext';

/** Render a finding's details as small expected-vs-found lists. */
function FindingDetails({ f }: { f: ReportQcFinding }): JSX.Element | null {
  const d = f.details;
  // Q1's details are two fingerprints: the message already says it all.
  if (!d || f.check === 'Q1') return null;
  const list = (key: string): Array<Record<string, unknown>> | null =>
    Array.isArray(d[key]) ? (d[key] as Array<Record<string, unknown>>) : null;
  const claims = list('claims');
  if (claims) {
    return (
      <ul className="qc-details">
        {claims.map((c, i) => (
          <li key={i}>
            <strong>{String(c.claim)}</strong> — “{String(c.sentence)}”
            <div className="muted">Data: {String(c.dataSays)}</div>
          </li>
        ))}
      </ul>
    );
  }
  const sourceTypes = list('sourceTypes');
  if (sourceTypes) {
    return (
      <table className="leads-table qc-details">
        <thead>
          <tr>
            <th>Source type</th>
            <th>Report</th>
            <th>Workbook “Yes” rows</th>
          </tr>
        </thead>
        <tbody>
          {sourceTypes.map((s) => (
            <tr key={String(s.sourceType)}>
              <td>{String(s.sourceType)}</td>
              <td>{String(s.report)}</td>
              <td>{String(s.workbook)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  const hosts = list('hosts');
  if (hosts) {
    return (
      <details>
        <summary className="muted">Show the {hosts.length} domain(s)</summary>
        <ul className="qc-details">
          {hosts.map((h) => (
            <li key={String(h.domain)}>
              {String(h.domain)} — {String(h.citations)} citation(s)
              {h.suggestion
                ? ` · research suggests ${String(h.suggestion)} (${String(h.confidence ?? '?')}%)`
                : ' · not researched'}
            </li>
          ))}
        </ul>
      </details>
    );
  }
  // Q8 lists sentences as strings; Q9 as { sentence, leader, measure }.
  if (Array.isArray(d.sentences)) {
    return (
      <ul className="qc-details">
        {(d.sentences as unknown[]).map((s, i) => {
          const o = typeof s === 'string' ? { sentence: s } : (s as Record<string, unknown>);
          return (
            <li key={i}>
              “{String(o.sentence)}”
              {o.leader ? (
                <div className="muted">
                  The leader is {String(o.leader)} ({String(o.measure)}).
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    );
  }
  const cells = list('cells');
  if (cells) {
    return (
      <ul className="qc-details">
        {cells.map((c, i) => (
          <li key={i}>
            {String(c.stage)} · prompt {String(c.prompt)} · {String(c.model)}
          </li>
        ))}
      </ul>
    );
  }
  const stages = list('stages');
  const rows = stages ?? Object.entries(d).map(([k, v]) => ({ [k]: v }));
  return (
    <ul className="qc-details">
      {rows.map((r, i) => (
        <li key={i} className="muted">
          {Object.entries(r)
            .map(([k, v]) => `${k}: ${typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
            .join(' · ')}
        </li>
      ))}
    </ul>
  );
}

export function ReportQcPanel({
  runId,
  companyName,
  onError,
}: {
  runId: string;
  companyName: string;
  onError: (message: string) => void;
}): JSX.Element | null {
  const { user } = useAuth();
  const canDecide = user?.role === 'admin' || user?.role === 'manager';
  const [data, setData] = useState<ReportQcPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'accepted' | 'rejected' | ''>('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.getReportQc(runId));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Could not load report QC');
    }
  }, [runId]);
  useEffect(() => {
    void load();
  }, [load]);
  // While a refresh is queued or running, look again every 10 s.
  useEffect(() => {
    if (!data?.refreshing) return;
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [data?.refreshing, load]);

  const act = async (label: string, fn: () => Promise<ReportQcPayload>): Promise<void> => {
    setBusy(label);
    try {
      setData(await fn());
      setError(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  const downloadDraft = async (): Promise<void> => {
    setBusy('draft');
    try {
      const res = await api.downloadBenchmarkReportDraftPdf(runId);
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        onError(body?.error?.message ?? `Draft PDF failed (${res.status}).`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aurora-benchmark-${companyName.replace(/\s+/g, '-').toLowerCase()}-DRAFT.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setBusy(null);
    }
  };

  if (error && !data) return <div className="form-error">{error}</div>;
  if (!data) return null;

  const check = data.check;
  const latestReview = data.reviews[0] ?? null;
  const blocking = check?.findings.filter((f) => f.severity === 'block') ?? [];
  const warnings = check?.findings.filter((f) => f.severity === 'warn') ?? [];
  const status = data.refreshing
    ? { label: 'Refreshing', cls: 'qc-review' }
    : !check
      ? { label: 'Not checked', cls: 'qc-review' }
      : latestReview?.decision === 'rejected'
        ? { label: 'Rejected', cls: 'qc-block' }
        : check.status === 'pass'
          ? { label: 'Passed', cls: 'qc-pass' }
          : latestReview?.decision === 'accepted'
            ? { label: 'Accepted', cls: 'qc-pass' }
            : { label: 'Needs review', cls: 'qc-block' };

  return (
    <div className="card report-qc-panel" id="report-qc">
      <h3>
        Report QC <span className={`qc-badge ${status.cls}`}>{status.label}</span>{' '}
        <span className="muted">
          {data.releasable ? 'client PDF releasable' : 'client PDF held'}
          {!data.enforced ? ' · not enforced' : ''}
        </span>
      </h3>
      <p className="field-hint">{data.why}</p>
      {check && (
        <p className="muted">
          Checked {new Date(check.createdAt).toLocaleString()}
          {check.reason ? ` (${check.reason})` : ''}
          {check.narrativeRetried ? ' · narrative regenerated once with corrections' : ''}
        </p>
      )}
      {!check && data.latestCheck && (
        <p className="muted">
          Last checked {new Date(data.latestCheck.createdAt).toLocaleString()} on earlier figures.
        </p>
      )}

      {blocking.length > 0 && (
        <>
          <h4>Holding the PDF</h4>
          {blocking.map((f, i) => (
            <div key={i} className="qc-finding qc-block">
              <span className="qc-badge qc-block">{f.check}</span> {data.checks[f.check]}: {f.message}
              <FindingDetails f={f} />
            </div>
          ))}
        </>
      )}
      {warnings.length > 0 && (
        <details>
          <summary>
            {warnings.length} note(s) — shown, never holding the PDF
          </summary>
          {warnings.map((f, i) => (
            <div key={i} className="qc-finding qc-review">
              <span className="qc-badge qc-review">{f.check}</span> {data.checks[f.check]}: {f.message}
              <FindingDetails f={f} />
            </div>
          ))}
        </details>
      )}

      {latestReview && (
        <p>
          <strong>{latestReview.decision === 'accepted' ? 'Accepted' : 'Rejected'}</strong> by{' '}
          {latestReview.reviewerName ?? latestReview.reviewerEmail ?? 'a reviewer'} on{' '}
          {new Date(latestReview.createdAt).toLocaleDateString()}: {latestReview.reason}
        </p>
      )}

      <div className="prompt-actions">
        {!data.releasable && (
          <button
            type="button"
            className="btn btn-small"
            disabled={busy !== null}
            onClick={() => void downloadDraft()}
            title="A copy stamped DRAFT, for review. The client never receives one."
          >
            {busy === 'draft' ? 'Preparing…' : 'Download draft PDF'}
          </button>
        )}
        {canDecide && (
          <>
            <button
              type="button"
              className="btn btn-small"
              disabled={busy !== null || data.refreshing}
              onClick={() => void act('Refresh', () => api.refreshReport(runId))}
              title="Re-check links and categories, recompute the figures, rewrite the narrative from them (never for a PL-1 archive run) and re-run the checks. No AI research is re-run."
            >
              {data.refreshing ? 'Refresh queued…' : 'Refresh report'}
            </button>
            <button
              type="button"
              className="btn btn-small btn-ghost"
              disabled={busy !== null}
              onClick={() => void act('Re-run checks', () => api.runReportQc(runId))}
            >
              {busy === 'Re-run checks' ? 'Checking…' : 'Re-run checks'}
            </button>
          </>
        )}
      </div>

      {canDecide && check && (
        <div className="reject-form">
          <select
            aria-label="Report QC decision"
            value={decision}
            onChange={(e) => setDecision(e.target.value as 'accepted' | 'rejected' | '')}
          >
            <option value="">Decide…</option>
            {check.status === 'fail' && <option value="accepted">Accept — release the PDF</option>}
            <option value="rejected">Reject — hold the PDF</option>
          </select>
          <input
            maxLength={2000}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (recorded with your name; stands until the figures change)"
          />
          <button
            type="button"
            className="btn btn-small btn-primary"
            disabled={busy !== null || !decision || reason.trim().length < 10}
            onClick={() =>
              void act('Record', async () => {
                const out = await api.reviewReportQc(runId, {
                  decision: decision as 'accepted' | 'rejected',
                  reason: reason.trim(),
                });
                setDecision('');
                setReason('');
                return out;
              })
            }
          >
            {busy === 'Record' ? 'Recording…' : 'Record'}
          </button>
        </div>
      )}
      {error && <div className="form-error">{error}</div>}
      {data.reviews.length > 1 && (
        <details>
          <summary className="muted">Earlier decisions on these figures ({data.reviews.length - 1})</summary>
          <ul className="prompt-history">
            {data.reviews.slice(1).map((r) => (
              <li key={r.id}>
                {r.decision} · {r.reviewerName ?? r.reviewerEmail ?? 'reviewer'} ·{' '}
                {new Date(r.createdAt).toLocaleString()} — {r.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
